import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Activity, Users, TrendingUp, RefreshCw, Globe, Smartphone, Monitor, Tablet,
  ArrowUpRight, ExternalLink, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
};

const deviceIcon = (d) => d === 'mobile' ? Smartphone : d === 'tablet' ? Tablet : Monitor;

const AnalyticsPage = () => {
  const [live, setLive] = useState({ active_visitors: 0, visitors: [] });
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [l, s, r] = await Promise.all([
        axios.get(`${API}/api/admin/analytics/live`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/analytics/summary?days=${days}`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/analytics/recent?limit=50`, { headers: authHeaders() }),
      ]);
      setLive(l.data);
      setSummary(s.data);
      setRecent(r.data.visits || []);
    } catch (e) {
      console.error('analytics load', e);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Refresh live + recent every 10s
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const [l, r] = await Promise.all([
          axios.get(`${API}/api/admin/analytics/live`, { headers: authHeaders() }),
          axios.get(`${API}/api/admin/analytics/recent?limit=50`, { headers: authHeaders() }),
        ]);
        setLive(l.data);
        setRecent(r.data.visits || []);
      } catch {}
    }, 10000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return <div className="p-8 text-stone-500">Cargando analítica...</div>;
  }

  const kpis = summary?.kpis || {};
  const buckets = summary?.buckets || [];
  const topPages = summary?.top_pages || [];
  const topReferers = summary?.top_referers || [];
  const devices = summary?.devices || {};
  const totalDev = Object.values(devices).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6 p-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-100">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Analítica web</h1>
            <p className="text-sm text-stone-500">Visitas en vivo · historial · top páginas</p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex gap-1 bg-stone-100 p-1 rounded-full">
            {[1, 7, 30].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  days === d ? 'bg-white text-stone-900 shadow' : 'text-stone-500 hover:text-stone-800'
                }`}
                data-testid={`range-${d}d`}
              >
                {d === 1 ? 'Hoy' : `${d}d`}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} className="rounded-full" data-testid="refresh-analytics">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Recargar
          </Button>
        </div>
      </div>

      {/* LIVE BANNER */}
      <div className="bg-gradient-to-r from-emerald-50 via-white to-teal-50 border border-emerald-100 rounded-3xl p-5 flex items-center gap-4 shadow-[0_4px_18px_rgba(16,185,129,0.08)]">
        <div className="relative">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center text-white">
            <Users className="w-6 h-6" />
          </div>
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full ring-2 ring-white animate-pulse" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">En vivo · últimos 5 min</p>
          <h2 className="text-3xl font-heading font-extrabold text-stone-900 mt-0.5">
            {live.active_visitors} <span className="text-base font-medium text-stone-500">visitante{live.active_visitors === 1 ? '' : 's'} activo{live.active_visitors === 1 ? '' : 's'}</span>
          </h2>
        </div>
        {live.visitors.length > 0 && (
          <div className="hidden md:flex flex-col text-right">
            <p className="text-xs text-stone-500">Página más vista ahora</p>
            <p className="font-mono text-sm font-semibold text-stone-900">
              {(() => {
                const counts = {};
                live.visitors.forEach(v => { counts[v.path] = (counts[v.path] || 0) + 1; });
                const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
                return top ? top[0] : '—';
              })()}
            </p>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="Visitas hoy" value={kpis.today_visits} icon={Activity} color="orange" />
        <KPI label="Visitas ayer" value={kpis.yesterday_visits} icon={Clock} color="violet" />
        <KPI label={`Visitas ${kpis.days}d`} value={kpis.total_visits} icon={TrendingUp} color="emerald" />
        <KPI label="Visitantes únicos" value={kpis.unique_visitors} icon={Users} color="sky" />
      </div>

      {/* Trend chart */}
      <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_4px_18px_rgba(0,0,0,0.04)] p-5">
        <h3 className="font-heading font-bold text-base mb-1">Tendencia de visitas</h3>
        <p className="text-xs text-stone-500 mb-4">Visitas vs visitantes únicos {days === 1 ? '(por hora)' : `(últimos ${days} días)`}</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#78716c' }} />
              <YAxis tick={{ fontSize: 11, fill: '#78716c' }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e7e5e4', fontSize: 12 }} />
              <Line type="monotone" dataKey="visits" stroke="#FF6B4A" strokeWidth={2.5} dot={{ r: 3 }} name="Visitas" />
              <Line type="monotone" dataKey="uniques" stroke="#7C3AED" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} name="Únicos" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top pages */}
        <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_4px_18px_rgba(0,0,0,0.04)] p-5">
          <h3 className="font-heading font-bold text-base mb-3">Páginas más visitadas</h3>
          <div className="space-y-2">
            {topPages.length === 0 && <p className="text-sm text-stone-400">Sin datos en este rango.</p>}
            {topPages.map((p) => {
              const maxV = topPages[0]?.visits || 1;
              return (
                <div key={p.path} className="flex items-center gap-3" data-testid="top-page-row">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono text-stone-800 truncate">{p.path}</p>
                    <div className="w-full bg-stone-100 rounded-full h-1.5 mt-1">
                      <div className="bg-gradient-to-r from-[#FF6B4A] to-orange-400 h-1.5 rounded-full" style={{ width: `${(p.visits / maxV) * 100}%` }} />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-stone-900">{p.visits}</p>
                    <p className="text-[10px] text-stone-500">{p.uniques} únicos</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top referers */}
        <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_4px_18px_rgba(0,0,0,0.04)] p-5">
          <h3 className="font-heading font-bold text-base mb-3">Fuentes de tráfico</h3>
          <div className="space-y-2">
            {topReferers.length === 0 && <p className="text-sm text-stone-400">Sin datos en este rango.</p>}
            {topReferers.map((r) => {
              const label = r.referer === 'directo' ? 'Directo / favorito'
                          : r.referer === '__internal__' ? 'Interno (misma web)'
                          : r.referer;
              return (
                <div key={r.referer} className="flex items-center justify-between gap-2 py-1.5 border-b border-stone-100 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {r.referer && r.referer !== 'directo' && r.referer !== '__internal__' ? (
                      <ExternalLink className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                    ) : (
                      <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    )}
                    <span className="text-sm text-stone-800 truncate">{label}</span>
                  </div>
                  <span className="text-sm font-bold text-stone-900 flex-shrink-0">{r.visits}</span>
                </div>
              );
            })}
          </div>
          {/* Devices */}
          <div className="mt-5 pt-4 border-t border-stone-100">
            <h4 className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 mb-3">Dispositivos</h4>
            <div className="grid grid-cols-3 gap-2">
              {['desktop', 'mobile', 'tablet'].map(d => {
                const Icon = deviceIcon(d);
                const v = devices[d] || 0;
                const pct = totalDev ? Math.round((v / totalDev) * 100) : 0;
                return (
                  <div key={d} className="text-center p-3 bg-stone-50 rounded-xl">
                    <Icon className="w-5 h-5 mx-auto text-stone-600" />
                    <p className="text-lg font-bold text-stone-900 mt-1">{v}</p>
                    <p className="text-[10px] uppercase tracking-wide text-stone-500">{d}</p>
                    <p className="text-[10px] text-stone-400">{pct}%</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Recent visits stream */}
      <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_4px_18px_rgba(0,0,0,0.04)] p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-base">Últimas visitas</h3>
          <span className="text-xs text-stone-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Actualización cada 10s
          </span>
        </div>
        <div className="overflow-x-auto -mx-2 px-2">
          <table className="w-full text-sm" data-testid="recent-visits-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-100">
                <th className="py-2 font-bold">Hora</th>
                <th className="py-2 font-bold">Página</th>
                <th className="py-2 font-bold">Fuente</th>
                <th className="py-2 font-bold">Dispositivo</th>
                <th className="py-2 font-bold">Navegador</th>
                <th className="py-2 font-bold">Visitante</th>
              </tr>
            </thead>
            <tbody>
              {recent.slice(0, 30).map(v => {
                const Icon = deviceIcon(v.device);
                return (
                  <tr key={v.id} className="border-b border-stone-50 last:border-0 hover:bg-stone-50/60">
                    <td className="py-2 text-stone-600 font-mono text-xs whitespace-nowrap">{formatTime(v.created_at)}</td>
                    <td className="py-2 font-mono text-stone-800 max-w-[200px] truncate">{v.path}</td>
                    <td className="py-2 text-stone-600 max-w-[180px] truncate">
                      {v.referer === 'directo' ? 'Directo' : v.referer === '__internal__' ? 'Interno' : v.referer}
                    </td>
                    <td className="py-2 text-stone-600"><Icon className="w-4 h-4 inline mr-1 text-stone-400" />{v.device}</td>
                    <td className="py-2 text-stone-600 max-w-[120px] truncate">{v.ua_short}</td>
                    <td className="py-2 text-stone-400 font-mono text-[10px]">{v.visitor_id?.slice(0, 8)}</td>
                  </tr>
                );
              })}
              {recent.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-stone-400">Sin visitas registradas todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-stone-400 text-center pt-2">
        🔒 Sólo se almacenan datos anónimos: ruta, dispositivo, navegador, hash de IP (no la IP real) y un identificador efímero de sesión.
      </p>
    </div>
  );
};

const KPI = ({ label, value, icon: Icon, color }) => {
  const palette = {
    orange: 'bg-orange-50 text-orange-600',
    violet: 'bg-violet-50 text-violet-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    sky: 'bg-sky-50 text-sky-600',
  };
  return (
    <div className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_10px_rgba(0,0,0,0.03)] p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${palette[color] || palette.orange}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-stone-500 truncate">{label}</p>
        <p className="text-xl font-heading font-extrabold text-stone-900 mt-0.5">{value ?? 0}</p>
      </div>
    </div>
  );
};

export default AnalyticsPage;
